import { Money } from "../money";
import type { FicaData, Jurisdiction } from "../../data/schemas";
import {
  bracketTax,
  bracketsFor,
  federalTaxDeductionFor,
  incomeRecaptureFor,
  personalCreditRateFor,
  personalExemptionFor,
  standardDeductionFor,
  standardDeductionPhaseOutFor,
  taxpayerCreditBaseFor,
} from "./brackets";
import { chooseFederalDeduction } from "./deductions";
import { computeFica } from "./fica";
import type {
  DeductionResult,
  FicaResult,
  JurisdictionTaxResult,
  LocalTaxLine,
  TaxInput,
  TaxResult,
} from "./types";

/** The datasets the evaluator composes. State is optional (federal-only is valid). */
export interface TaxContext {
  federal: Jurisdiction;
  state?: Jurisdiction;
  fica: FicaData;
}

/** Probe amount (in wages) used to measure the combined marginal rate. */
const MARGINAL_PROBE = 100;

function clampZero(m: Money): Money {
  return m.isNegative() ? Money.zero() : m;
}

function round(value: number, places: number): number {
  const factor = 10 ** places;
  return Math.round(value * factor) / factor;
}

/** Income-tax for one jurisdiction, plus its deduction and taxable income. */
interface JurisdictionComputation {
  taxableIncome: Money;
  deduction: DeductionResult;
  incomeTax: Money;
}

function computeFederal(
  input: TaxInput,
  agi: Money,
  federal: Jurisdiction,
): JurisdictionComputation {
  const standard = Money.from(standardDeductionFor(federal, input.filingStatus));
  const deduction = chooseFederalDeduction(
    input.deductionMode ?? "auto",
    standard,
    input.itemized ?? {},
    agi,
  );
  const taxableIncome = clampZero(agi.subtract(deduction.amount));
  const incomeTax = bracketTax(taxableIncome, bracketsFor(federal, input.filingStatus));
  return { taxableIncome, deduction, incomeTax };
}

function computeState(
  input: TaxInput,
  agi: Money,
  state: Jurisdiction,
  federalDeduction: Money,
  federalIncomeTax: Money,
): { computation: JurisdictionComputation; localLines: LocalTaxLine[] } {
  if (!state.hasIncomeTax) {
    return {
      computation: {
        taxableIncome: Money.zero(),
        deduction: { kind: "standard", amount: Money.zero() },
        incomeTax: Money.zero(),
      },
      localLines: [],
    };
  }

  let standard = Money.from(standardDeductionFor(state, input.filingStatus));
  // Sliding standard deduction: the deduction phases down linearly with AGI in
  // one of two equivalent forms (see StandardDeductionPhaseOutSchema):
  //  • divisor (South Carolina's SCIAD, S.C. Code §12-6-1140(15)): reduce by
  //    `standard × (AGI − threshold) / divisor`, proportional to the deduction.
  //  • reductionRate (Wisconsin, Wis. Stat. §71.05(23)(a)): reduce by
  //    `rate × (AGI − threshold)`, a flat percentage of income above the
  //    threshold (single 12%, joint 19.778%), independent of the deduction.
  // Both are full at/below the threshold; above it the deduction slides toward
  // the status `floor` (zero unless set — Alabama floors at $5,000 joint /
  // $2,500 otherwise, Ala. Code §40-18-15(b)). The reduction rounds down to the
  // nearest `roundReductionDownTo` dollars where the statute requires it (SC: $10).
  const phaseOut = standardDeductionPhaseOutFor(state, input.filingStatus);
  if (phaseOut) {
    const over = agi.toNumber() - phaseOut.agiThreshold;
    if (over > 0) {
      const rawReduction =
        phaseOut.divisor !== undefined
          ? standard.multiply(over).divide(phaseOut.divisor).toNumber()
          : over * phaseOut.reductionRate!;
      const step = state.standardDeductionPhaseOut?.roundReductionDownTo;
      const reduction = step ? Math.floor(rawReduction / step) * step : rawReduction;
      standard = clampZero(standard.subtract(reduction));
      if (phaseOut.floor !== undefined && standard.lessThan(phaseOut.floor)) {
        standard = Money.from(phaseOut.floor);
      }
    }
  }
  const exemption = Money.from(personalExemptionFor(state, input.filingStatus));
  // Federal income tax paid is deductible against state taxable income in a few
  // states — uncapped (Alabama, Ala. Code §40-18-15(a)(1)) or capped and
  // AGI-phased (Oregon, ORS §316.680/§316.695). Zero where the state has no such
  // deduction. Subtracted before the brackets, like the standard deduction.
  const fedTaxDeduction = federalTaxDeductionFor(state, input.filingStatus, federalIncomeTax, agi);
  const taxableIncome = clampZero(
    agi.subtract(standard).subtract(exemption).subtract(fedTaxDeduction),
  );

  let incomeTax = bracketTax(taxableIncome, bracketsFor(state, input.filingStatus));

  // Special rules, e.g. the California 1% mental-health-services surtax.
  for (const rule of state.specialRules ?? []) {
    if (rule.surtaxRate !== undefined && rule.incomeThreshold !== undefined) {
      if (taxableIncome.greaterThan(rule.incomeThreshold)) {
        incomeTax = incomeTax.add(
          taxableIncome.subtract(rule.incomeThreshold).multiply(rule.surtaxRate),
        );
      }
    }
  }

  // High-income benefit recapture (Arkansas's bracket adjustment; Connecticut's
  // 2% phase-out add-back + tax recapture): one or more ramps, each adding a flat
  // amount that phases in over an income band and holds above, so a high earner
  // forfeits the benefit of the lower brackets. Exact below/above each ramp; a
  // small linear-vs-step residual only inside a ramp's band.
  const recapture = incomeRecaptureFor(state, input.filingStatus, taxableIncome.toNumber());
  if (recapture > 0) incomeTax = incomeTax.add(recapture);

  // Personal tax credit as a fraction of the tax that slides down with AGI
  // (Connecticut's Table E): the Connecticut income tax is `tax × (1 − rate)`,
  // applied AFTER the recapture (the worksheet credits the recapture-inclusive
  // total). The rate is 0 — no change — for jurisdictions without this credit.
  const creditRate = personalCreditRateFor(state, input.filingStatus, agi.toNumber());
  if (creditRate > 0) incomeTax = incomeTax.multiply(1 - creditRate);

  // Taxpayer tax credit (the Utah pattern): a nonrefundable credit standing in
  // for a standard deduction. The state taxes AGI directly (its standard
  // deduction is 0 above), then credits back `creditRate` of the *federal*
  // deduction, phased out at `phaseOutRate` of taxable income over a
  // filing-status base, floored at zero — so it never refunds (Utah Code
  // §59-10-1018; TC-40 worksheet). The phase-out naturally raises the effective
  // marginal rate in its band, which the $100 wage probe measures correctly.
  const credit = state.taxpayerCredit;
  if (credit) {
    const initial = federalDeduction.multiply(credit.creditRate);
    const base = taxpayerCreditBaseFor(state, input.filingStatus);
    const overBase = clampZero(taxableIncome.subtract(base));
    const reduced = clampZero(initial.subtract(overBase.multiply(credit.phaseOutRate)));
    incomeTax = clampZero(incomeTax.subtract(reduced));
  }

  // Local add-ons apply only when the caller opts in by id (a NYC resident, say).
  const selected = new Set(input.localJurisdictionIds ?? []);
  const localLines: LocalTaxLine[] = [];
  for (const addOn of state.localAddOns ?? []) {
    if (!selected.has(addOn.id)) continue;
    let tax = Money.zero();
    if (addOn.brackets && addOn.brackets.length > 0) {
      tax = bracketTax(taxableIncome, addOn.brackets);
    } else if (addOn.flatRate !== undefined) {
      tax = taxableIncome.multiply(addOn.flatRate);
    }
    localLines.push({ id: addOn.id, name: addOn.name, tax });
  }

  return {
    computation: {
      taxableIncome,
      deduction: { kind: "standard", amount: standard.add(exemption).add(fedTaxDeduction) },
      incomeTax,
    },
    localLines,
  };
}

/** All money pieces for a given input — reused to measure the marginal rate. */
interface Breakdown {
  grossIncome: Money;
  agi: Money;
  federal: JurisdictionComputation;
  fica: FicaResult;
  state: JurisdictionComputation | null;
  localLines: LocalTaxLine[];
  localTotal: Money;
  totalTax: Money;
}

function computeBreakdown(input: TaxInput, ctx: TaxContext): Breakdown {
  const wages = Money.from(input.wages);
  const grossIncome = wages.add(input.otherIncome ?? 0);
  const agi = clampZero(grossIncome.subtract(input.adjustments ?? 0));

  const federal = computeFederal(input, agi, ctx.federal);
  const fica = computeFica(wages, input.filingStatus, ctx.fica);

  let state: JurisdictionComputation | null = null;
  let localLines: LocalTaxLine[] = [];
  if (ctx.state) {
    const s = computeState(input, agi, ctx.state, federal.deduction.amount, federal.incomeTax);
    state = s.computation;
    localLines = s.localLines;
  }
  const localTotal = localLines.reduce((sum, l) => sum.add(l.tax), Money.zero());

  const totalTax = federal.incomeTax
    .add(fica.total)
    .add(state ? state.incomeTax : Money.zero())
    .add(localTotal);

  return { grossIncome, agi, federal, fica, state, localLines, localTotal, totalTax };
}

/**
 * Evaluate federal, FICA, state, and local taxes for one filer, composing them
 * into a single {@link TaxResult} with a citation on every line (BUILD-SPEC.md
 * §3, §8, §9). Deterministic: a pure function of the input and the datasets.
 *
 * The combined marginal rate is measured by re-running the computation with a
 * small wage probe, so it correctly reflects bracket boundaries, the Social
 * Security wage base, and the Additional Medicare threshold all at once.
 */
export function evaluateTaxes(input: TaxInput, ctx: TaxContext): TaxResult {
  const b = computeBreakdown(input, ctx);

  const bumped = computeBreakdown({ ...input, wages: input.wages + MARGINAL_PROBE }, ctx);
  const marginalRate = round(
    bumped.totalTax.subtract(b.totalTax).divide(MARGINAL_PROBE).toNumber(),
    6,
  );
  const effectiveRate = b.grossIncome.isZero()
    ? 0
    : round(b.totalTax.divide(b.grossIncome.toNumber()).toNumber(), 6);

  const federalResult: JurisdictionTaxResult = {
    jurisdictionId: ctx.federal.id,
    jurisdictionName: ctx.federal.name,
    taxableIncome: b.federal.taxableIncome,
    deduction: b.federal.deduction,
    incomeTax: b.federal.incomeTax,
    citation: ctx.federal.citation,
  };

  const stateResult: JurisdictionTaxResult | null =
    ctx.state && b.state
      ? {
          jurisdictionId: ctx.state.id,
          jurisdictionName: ctx.state.name,
          taxableIncome: b.state.taxableIncome,
          deduction: b.state.deduction,
          incomeTax: b.state.incomeTax,
          citation: ctx.state.citation,
        }
      : null;

  return {
    filingStatus: input.filingStatus,
    grossIncome: b.grossIncome,
    agi: b.agi,
    federal: federalResult,
    fica: b.fica,
    state: stateResult,
    local: {
      lines: b.localLines,
      total: b.localTotal,
      citation: b.localLines.length > 0 && ctx.state ? ctx.state.citation : null,
    },
    totals: {
      totalTax: b.totalTax,
      takeHome: b.grossIncome.subtract(b.totalTax),
      marginalRate,
      effectiveRate,
    },
  };
}
